import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PostCard from '../components/PostCard';
import FloatingBubbles from '../components/FloatingBubbles';
import axios from 'axios';

export default function Profile() {
  const { username } = useParams();
  const { API, token, user: currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({ followers: 0, following: 0, posts: 0 });
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ username: '', bio: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [avatarLoading, setAvatarLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [profileRes, postsRes, statsRes] = await Promise.all([
        axios.get(`${API}/users/${username}`),
        axios.get(`${API}/users/${username}/posts`, { headers }),
        axios.get(`${API}/users/${username}/stats`, { headers })
      ]);
      setProfile(profileRes.data);
      setPosts(postsRes.data);
      setStats(statsRes.data);
      setFollowing(statsRes.data.is_following || false);
      setEditForm({ username: profileRes.data.username, bio: profileRes.data.bio || '' });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [API, username, token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleFollow = async () => {
    if (!token) return;
    try {
      const res = await axios.post(`${API}/users/${username}/follow`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFollowing(res.data.following);
      setStats(prev => ({
        ...prev,
        followers: res.data.following ? prev.followers + 1 : prev.followers - 1
      }));
    } catch (err) { console.error(err); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError('');
    try {
      await axios.put(`${API}/users/me/update`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditing(false);
      if (editForm.username !== username) {
        navigate(`/profile/${editForm.username}`);
      } else {
        fetchAll();
      }
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setEditLoading(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/users/me/avatar`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      fetchAll();
    } catch (err) {
      console.error(err);
    } finally {
      setAvatarLoading(false);
    }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '80px', color: 'var(--muted)' }}>Chargement...</div>
  );

  if (!profile) return (
    <div style={{ textAlign: 'center', padding: '80px', color: 'var(--muted)' }}>Utilisateur introuvable.</div>
  );

  const initials = profile.username.slice(0, 2).toUpperCase();
  const joined = new Date(profile.created_at).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const isMe = currentUser?.username === username;
  const avatarUrl = profile.avatar ? `https://social-agora.com${profile.avatar}` : null;

  return (
    <>
      <FloatingBubbles />

      <div style={{ position: 'relative', zIndex: 1, minHeight: '100vh' }}>

        {/* Hero */}
        <div style={{
          background: 'rgba(235,244,255,0.85)',
          backdropFilter: 'blur(4px)',
          padding: '48px 20px 40px',
          textAlign: 'center',
          borderBottom: '1px solid rgba(190,227,248,0.5)'
        }}>

          {/* Avatar */}
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: '16px' }}>
            {avatarUrl ? (
              <img src={avatarUrl} alt={profile.username} style={{
                width: '88px', height: '88px', borderRadius: '50%',
                objectFit: 'cover', border: '4px solid #fff',
                boxShadow: '0 8px 24px rgba(43,108,176,0.3)'
              }} />
            ) : (
              <div style={{
                width: '88px', height: '88px', borderRadius: '50%',
                background: 'var(--blue)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontWeight: 800, fontSize: '26px',
                color: '#fff', border: '4px solid #fff',
                boxShadow: '0 8px 24px rgba(43,108,176,0.3)'
              }}>
                {initials}
              </div>
            )}

            {/* Upload button overlay */}
            {isMe && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handleAvatarChange}
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => fileInputRef.current.click()}
                  disabled={avatarLoading}
                  style={{
                    position: 'absolute', bottom: '2px', right: '2px',
                    width: '26px', height: '26px', borderRadius: '50%',
                    background: 'var(--blue)', color: '#fff',
                    border: '2px solid #fff', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', transition: 'all .2s',
                    boxShadow: '0 2px 8px rgba(43,108,176,0.4)'
                  }}
                  title="Changer la photo"
                >
                  {avatarLoading ? '...' : '✎'}
                </button>
              </>
            )}
          </div>

          <h1 style={{ fontSize: '26px', fontWeight: 800, color: 'var(--dark)', marginBottom: '6px', letterSpacing: '-0.5px' }}>
            {profile.username}
          </h1>

          {profile.bio && !editing && (
            <p style={{ fontSize: '15px', color: 'var(--text)', maxWidth: '400px', margin: '0 auto 12px', lineHeight: 1.7 }}>
              {profile.bio}
            </p>
          )}

          {!editing && (
            <p style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '20px' }}>
              Membre depuis {joined}
            </p>
          )}

          {/* Edit form */}
          {editing && (
            <form onSubmit={handleEdit} style={{ maxWidth: '360px', margin: '0 auto 20px' }}>
              {editError && (
                <div style={{
                  background: '#FFF5F5', color: 'var(--red)',
                  padding: '10px 14px', borderRadius: '10px',
                  marginBottom: '12px', fontSize: '13px',
                  border: '1px solid #FED7D7'
                }}>
                  {editError}
                </div>
              )}
              <input
                value={editForm.username}
                onChange={e => setEditForm({ ...editForm, username: e.target.value })}
                placeholder="Nom d'utilisateur"
                style={{
                  width: '100%', padding: '11px 16px',
                  border: '1.5px solid var(--blue-border)', borderRadius: '12px',
                  fontSize: '14px', outline: 'none', marginBottom: '10px',
                  background: 'rgba(255,255,255,0.9)', textAlign: 'left'
                }}
                onFocus={e => e.target.style.borderColor = 'var(--blue)'}
                onBlur={e => e.target.style.borderColor = 'var(--blue-border)'}
              />
              <textarea
                value={editForm.bio}
                onChange={e => setEditForm({ ...editForm, bio: e.target.value })}
                placeholder="Ta bio — parle de tes passions..."
                rows={3}
                style={{
                  width: '100%', padding: '11px 16px',
                  border: '1.5px solid var(--blue-border)', borderRadius: '12px',
                  fontSize: '14px', outline: 'none', resize: 'none',
                  marginBottom: '12px', background: 'rgba(255,255,255,0.9)',
                  fontFamily: 'inherit', lineHeight: 1.6, textAlign: 'left'
                }}
                onFocus={e => e.target.style.borderColor = 'var(--blue)'}
                onBlur={e => e.target.style.borderColor = 'var(--blue-border)'}
              />
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <button type="submit" disabled={editLoading} style={{
                  background: 'var(--blue)', color: '#fff', border: 'none',
                  padding: '10px 24px', borderRadius: '24px', fontSize: '14px',
                  fontWeight: 600, cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(43,108,176,0.3)'
                }}>
                  {editLoading ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
                <button type="button" onClick={() => setEditing(false)} style={{
                  background: '#fff', color: 'var(--text)',
                  border: '1px solid var(--border)',
                  padding: '10px 24px', borderRadius: '24px',
                  fontSize: '14px', fontWeight: 600, cursor: 'pointer'
                }}>
                  Annuler
                </button>
              </div>
            </form>
          )}

          {/* Action buttons */}
          {!editing && (
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              {isMe ? (
                <>
                <button onClick={() => setEditing(true)} style={{
                  background: '#fff', color: 'var(--blue)',
                  border: '1.5px solid var(--blue)',
                  padding: '10px 24px', borderRadius: '24px',
                  fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  transition: 'all .2s'
                }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--blue-bg)'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  Modifier le profil
                </button>
              <button onClick={async () => {
                if (!window.confirm("Supprimer définitivement ton compte ? Cette action est irréversible.")) return;
                try {
                  await axios.delete(`${API}/users/me`, { headers: { Authorization: `Bearer ${token}` } });
                  logout();
                  navigate("/");
                } catch (err) { console.error(err); }
              }} style={{ background: "#FFF5F5", color: "var(--red)", border: "1px solid #FED7D7", padding: "10px 20px", borderRadius: "24px", fontSize: "13px", fontWeight: 600, cursor: "pointer", marginLeft: "8px" }}>
                🗑️ Supprimer mon compte
              </button>
              </>
              ) : token && (
                <button onClick={handleFollow} style={{
                  background: following ? '#fff' : 'var(--blue)',
                  color: following ? 'var(--blue)' : '#fff',
                  border: '1.5px solid var(--blue)',
                  padding: '10px 28px', borderRadius: '24px',
                  fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  transition: 'all .2s',
                  boxShadow: following ? 'none' : '0 4px 12px rgba(43,108,176,0.3)'
                }}>
                  {following ? '✓ Abonné' : 'Suivre →'}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Stats bar */}
        <div style={{
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid var(--border)',
          position: 'sticky', top: '64px', zIndex: 10
        }}>
          <div style={{ maxWidth: '680px', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
            {[
              { label: 'Posts', value: stats.posts },
              { label: 'Followers', value: stats.followers },
              { label: 'Abonnements', value: stats.following }
            ].map((s, i) => (
              <div key={i} style={{
                flex: 1, textAlign: 'center', padding: '16px 12px',
                borderRight: i < 2 ? '1px solid var(--border)' : 'none',
                maxWidth: '160px'
              }}>
                <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--blue)' }}>{s.value}</div>
                <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Posts */}
        <div style={{ maxWidth: '680px', margin: '0 auto', padding: '32px 20px 48px' }}>
          {posts.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '60px 20px',
              background: 'rgba(255,255,255,0.8)',
              borderRadius: 'var(--radius)',
              border: '1px solid var(--border)'
            }}>
              <p style={{ fontSize: '16px', color: 'var(--muted)', fontWeight: 500 }}>
                {isMe ? "Tu n'as pas encore posté." : "Aucun post pour l'instant."}
              </p>
            </div>
          ) : (
            posts.map((post, i) => (
              <div key={post.id} style={{ animation: `fadeUp 0.5s ease ${i * 0.05}s both` }}>
                <PostCard post={post} onUpdate={fetchAll} />
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
